// API client with automatic static-JSON fallback for offline / outage mode.
// If the live API at API_BASE fails or times out, we fall back to /blogs-fallback.json
// so the site never appears broken.

// IMPORTANT: must be HTTPS for production — browsers refuse to follow
// http->https redirects on CORS preflight requests.
const API_BASE =
  process.env.REACT_APP_API_BASE || "https://rashiddev.in/api";

const REQUEST_TIMEOUT_MS = 6000;

const FALLBACK_URL = "/blogs-fallback.json";
const RESUME_FALLBACK_URL = "/resumeData.json";

let fallbackCache = null;
let resumeFallbackCache = null;

async function loadFallback() {
  if (fallbackCache) return fallbackCache;
  try {
    const res = await fetch(FALLBACK_URL);
    if (!res.ok) throw new Error("Fallback fetch failed");
    fallbackCache = await res.json();
    return fallbackCache;
  } catch (e) {
    console.warn("Fallback JSON unavailable", e);
    return {blogs: [], users: [], comments: []};
  }
}

function withTimeout(promise, ms, signal) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error("Request timed out")), ms);
    // Honor an externally-supplied abort signal so the caller's race wins
    // over the timeout race — we want a proper AbortError, not "timed out".
    const onAbort = () => {
      clearTimeout(t);
      const err = new Error("Aborted");
      err.name = "AbortError";
      reject(err);
    };
    if (signal) {
      if (signal.aborted) return onAbort();
      signal.addEventListener("abort", onAbort, {once: true});
    }
    promise.then(
      v => { clearTimeout(t); resolve(v); },
      e => { clearTimeout(t); reject(e); }
    );
  });
}

// ---------- Site-wide API health signal ----------
// Any 5xx / network failure broadcasts "degraded"; the next healthy response
// broadcasts "ok". MaintenanceBanner (shared/ui) listens and shows/hides a
// friendly notice — no component needs to thread outage state through props.
function announceApiHealth(ok) {
  try {
    window.dispatchEvent(new CustomEvent(ok ? "rd:api-ok" : "rd:api-degraded"));
  } catch (_) { /* SSR / very old browsers — banner just won't show */ }
}

async function request(path, options = {}, token = null) {
  const url = `${API_BASE}${path}`;
  // `quiet` requests (e.g. the chatbot, which can be intentionally
  // unconfigured) never trigger the site-wide maintenance banner.
  const {signal, quiet, ...rest} = options;
  const headers = {
    "Content-Type": "application/json",
    ...(rest.headers || {})
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  let res;
  try {
    const fetchPromise = fetch(url, {...rest, headers, signal});
    res = await withTimeout(fetchPromise, REQUEST_TIMEOUT_MS, signal);
  } catch (e) {
    // Network down / DNS / timeout — the API is unreachable.
    if (!quiet && e.name !== "AbortError" && !signal?.aborted) announceApiHealth(false);
    throw e;
  }
  const text = await res.text().catch(() => "");
  // Try to parse JSON either way — our PHP returns JSON even on 500.
  let body = null;
  try { body = text ? JSON.parse(text) : null; } catch (_) { /* not JSON */ }

  if (!quiet) announceApiHealth(res.ok || res.status < 500);

  if (!res.ok) {
    // Surface the real PHP/SQL message when available.
    const msg = body?.message || body?.error || text || res.statusText;
    const err = new Error(`API ${res.status}: ${msg}`);
    err.status = res.status;
    err.body = body;
    console.error("[API error]", url, body || text);
    throw err;
  }
  return body;
}

// ---------- Public API with graceful fallback ----------

// ---- Helpers used by the offline (static JSON) fallback so search/filter/sort
//      still work when the PHP backend is unreachable.
function applyStaticFilters(list, {q = "", sortBy = "created", dir = "desc", categories = []} = {}) {
  let out = [...(list || [])];

  // Category multi-select (IN-list semantics)
  if (categories && categories.length) {
    const set = new Set(categories.map(c => c.toLowerCase()));
    out = out.filter(b => set.has((b.category || "").toLowerCase()));
  }

  // CONTEXTUAL search — happens AFTER category filter so search stays inside
  // the selected categories.
  if (q) {
    const needle = q.toLowerCase();
    const titleHits = out.filter(b => (b.title || "").toLowerCase().includes(needle));
    out = titleHits.length
      ? titleHits
      : out.filter(b =>
          (b.title || "").toLowerCase().includes(needle) ||
          (b.content || "").toLowerCase().includes(needle)
        );
  }

  // Sort
  const cmpField = {
    created: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    likes:   (a, b) => (a.likes || 0) - (b.likes || 0),
    views:   (a, b) => (a.views || 0) - (b.views || 0),
  }[sortBy] || ((a, b) => new Date(a.created_at) - new Date(b.created_at));

  out.sort((a, b) => {
    const c = cmpField(a, b);
    return dir === "desc" ? -c : c;
  });
  return out;
}

export async function fetchBlogs({
  page = 1,
  perPage = 15,
  cursor = "",
  sortBy = "created",
  dir = "desc",
  q = "",
  categories = [],
  signal,
} = {}) {
  const qs = new URLSearchParams();
  qs.set("page", String(page));
  qs.set("per_page", String(perPage));
  if (cursor) qs.set("cursor", cursor);
  qs.set("sort_by", sortBy);
  qs.set("dir", dir);
  if (q) qs.set("q", q);
  // CRITICAL: PHP collapses repeated `?key=` to the LAST value unless the
  // key ends in `[]`. Use `category[]=A&category[]=B` so PHP populates
  // $_GET['category'] as an actual array.
  (categories || []).forEach(c => { if (c) qs.append("category[]", c); });

  try {
    return await request(`/blogs.php?${qs.toString()}`, {signal});
  } catch (e) {
    // If the caller aborted (route change, new search), don't fall back —
    // just propagate so the caller can ignore it.
    if (e.name === "AbortError" || signal?.aborted) throw e;
    // GOLDEN RULE: fallback to static JSON so the UI never goes blank.
    console.warn("[fallback] fetchBlogs", e.message);
    const fb = await loadFallback();
    const filtered = applyStaticFilters(fb.blogs, {q, sortBy, dir, categories});
    const start = (page - 1) * perPage;
    const slice = filtered.slice(start, start + perPage + 1);
    const hasMore = slice.length > perPage;
    const pageRows = hasMore ? slice.slice(0, perPage) : slice;
    return {
      blogs: pageRows,
      total: filtered.length,
      page,
      per_page: perPage,
      sort_by: sortBy,
      dir,
      q,
      categories,
      has_more: hasMore,
      next_cursor: null,
      offline: true,
    };
  }
}

// Distinct categories with post counts. Falls back to deriving them from
// the static JSON if /categories.php is unreachable.
export async function fetchCategories({signal} = {}) {
  try {
    return await request(`/categories.php`, {signal});
  } catch (e) {
    if (e.name === "AbortError" || signal?.aborted) throw e;
    console.warn("[fallback] fetchCategories", e.message);
    const fb = await loadFallback();
    const counts = new Map();
    (fb.blogs || []).forEach(b => {
      const c = (b.category || "").trim();
      if (!c) return;
      counts.set(c, (counts.get(c) || 0) + 1);
    });
    const categories = [...counts.entries()]
      .map(([name, count]) => ({name, count}))
      .sort((a, b) => a.name.localeCompare(b.name));
    return {categories, offline: true};
  }
}

// `token` is optional — a logged-in admin passes theirs so a draft post can
// still be opened for preview/editing (see api/blog.php); a public reader
// calls this with no token and only ever sees published posts.
export async function fetchBlog(id, token = null) {
  try {
    return await request(`/blog.php?id=${encodeURIComponent(id)}`, {}, token);
  } catch (e) {
    console.warn("[fallback] fetchBlog", e.message);
    const fb = await loadFallback();
    const blog = (fb.blogs || []).find(b => String(b.id) === String(id));
    if (!blog) throw new Error("Blog not found (offline mode)");
    return {blog, comments: [], offline: true};
  }
}

// "Load more comments" — every page after the first (fetchBlog already
// returns the first page embedded in the blog detail response).
export async function fetchComments(blogId, offset, limit = 10) {
  return request(
    `/comment.php?action=list&blog_id=${encodeURIComponent(blogId)}&offset=${offset}&limit=${limit}`
  );
}

// Persistent per-browser identifier used for guest view dedup.
function getClientToken() {
  let t = null;
  try { t = localStorage.getItem("rd_client_token"); } catch (_) {}
  if (!t) {
    t =
      (crypto?.randomUUID && crypto.randomUUID()) ||
      Math.random().toString(36).slice(2) + Date.now().toString(36);
    try { localStorage.setItem("rd_client_token", t); } catch (_) {}
  }
  return t;
}

export async function incrementView(id, token) {
  try {
    return await request(
      `/view.php`,
      {
        method: "POST",
        body: JSON.stringify({blog_id: id, client_token: getClientToken()})
      },
      token
    );
  } catch (e) {
    return {ok: false, offline: true};
  }
}

export async function likeBlog(id, token) {
  try {
    return await request(
      `/like.php`,
      {method: "POST", body: JSON.stringify({blog_id: id})},
      token
    );
  } catch (e) {
    return {ok: false, offline: true};
  }
}

export async function adminDeleteComment(commentId, token) {
  return request(
    `/admin.php?action=comment_delete&id=${commentId}`,
    {method: "POST"},
    token
  );
}

export async function postComment(blogId, text, token, parentId = null) {
  return request(
    `/comment.php`,
    {
      method: "POST",
      body: JSON.stringify({blog_id: blogId, comment_text: text, parent_id: parentId})
    },
    token
  );
}

// ---------- Site settings ----------
export async function fetchSettings() {
  try {
    return await request(`/settings.php`);
  } catch (e) {
    console.warn("[settings] using defaults — API unreachable", e.message);
    return {settings: null, offline: true};
  }
}

export async function adminGetSettings(token) {
  return request(`/admin.php?action=settings_get`, {}, token);
}

export async function adminUpdateSettings(settings, token) {
  return request(
    `/admin.php?action=settings_update`,
    {method: "POST", body: JSON.stringify({settings})},
    token
  );
}

// ---------- Resume ----------
async function loadResumeFallback() {
  if (resumeFallbackCache) return resumeFallbackCache;
  try {
    const res = await fetch(RESUME_FALLBACK_URL);
    if (!res.ok) throw new Error("Resume fallback fetch failed");
    resumeFallbackCache = await res.json();
    return resumeFallbackCache;
  } catch (e) {
    console.warn("[resume] static fallback unavailable", e?.message);
    return null;
  }
}

export async function fetchResume() {
  try {
    const res = await request(`/resume.php`);
    if (res?.resume && Object.keys(res.resume).length > 0) {
      return {
        resume: res.resume,
        pdfEnabled: res.pdfEnabled !== false,
        offline: false
      };
    }
    // API reachable but empty content → use static JSON.
    const fb = await loadResumeFallback();
    return {resume: fb, pdfEnabled: res?.pdfEnabled !== false, offline: false};
  } catch (e) {
    console.warn("[resume] API unreachable, using static JSON", e?.message);
    const fb = await loadResumeFallback();
    return {resume: fb, pdfEnabled: true, offline: true};
  }
}

// Fire-and-forget tracker. Never throws.
export async function trackResumeEvent(event) {
  try {
    let clientToken = null;
    try { clientToken = localStorage.getItem("rd_client_token"); } catch (_) {}
    await request(`/resume.php?action=track`, {
      method: "POST",
      body: JSON.stringify({event, client_token: clientToken})
    });
  } catch (_) { /* analytics is best-effort */ }
}

export async function adminGetResume(token) {
  return request(`/admin.php?action=resume_get`, {}, token);
}

export async function adminUpdateResume(resume, token) {
  return request(
    `/admin.php?action=resume_update`,
    {method: "POST", body: JSON.stringify({resume})},
    token
  );
}

export async function adminGetResumeAnalytics(token) {
  return request(`/admin.php?action=resume_analytics`, {}, token);
}

// Paginated "who viewed/downloaded, when, from which IP" log — fetched
// lazily (only once the admin opens that panel), page by page.
export async function adminGetResumeEvents(offset, limit, token) {
  return request(
    `/admin.php?action=resume_events&offset=${offset}&limit=${limit}`,
    {},
    token
  );
}

// ---------- Admin: site-wide visitor analytics (Task 1 dashboard) ----------
export async function adminGetVisitorStats(token) {
  return request(`/admin.php?action=visitor_stats`, {}, token);
}

// Paginated guest + logged-in activity log, optionally filtered to one
// event_type (page_view, contact_click, resume_view, …).
export async function adminGetVisitorLog(offset, limit, token, event = "") {
  const eventParam = event ? `&event=${encodeURIComponent(event)}` : "";
  return request(
    `/admin.php?action=visitor_log_list&offset=${offset}&limit=${limit}${eventParam}`,
    {},
    token
  );
}

// Paginated per-account login history, optionally scoped to one user.
export async function adminGetUserSessions(offset, limit, token, userId = null) {
  const userParam = userId ? `&user_id=${userId}` : "";
  return request(
    `/admin.php?action=user_sessions_list&offset=${offset}&limit=${limit}${userParam}`,
    {},
    token
  );
}

// ---------- Admin: users ----------
export async function adminListUsers(token, {offset = 0, limit = 20} = {}) {
  return request(`/admin.php?action=users_list&offset=${offset}&limit=${limit}`, {}, token);
}

export async function adminSetUserActive(id, isActive, token) {
  return request(
    `/admin.php?action=user_set_active&id=${id}`,
    {method: "POST", body: JSON.stringify({is_active: isActive})},
    token
  );
}

// ---------- Contact form ----------
// Public submission. Backend always returns 200 on a successful DB write,
// even if email delivery fails — see api/contact.php.
export async function submitContactMessage(payload) {
  return request(`/contact.php`, {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

// ---------- Admin: contact inbox ----------
export async function adminListMessages(token, {offset = 0, limit = 20} = {}) {
  return request(`/admin.php?action=messages_list&offset=${offset}&limit=${limit}`, {}, token);
}

export async function adminMarkMessageRead(id, isRead, token) {
  return request(
    `/admin.php?action=message_mark_read&id=${id}`,
    {method: "POST", body: JSON.stringify({is_read: isRead})},
    token
  );
}

export async function adminDeleteMessage(id, token) {
  return request(
    `/admin.php?action=message_delete&id=${id}`,
    {method: "POST"},
    token
  );
}

// Best-effort — a server can't know the visitor's local timezone from an
// HTTP request alone, so it rides along on login/register the same way it
// already does on trackPageView() (see @shared/lib/analytics). Feeds the
// per-account session-history row in `user_sessions`.
function clientTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch (_) {
    return null;
  }
}

export async function login(username, password) {
  return request(`/auth.php?action=login`, {
    method: "POST",
    body: JSON.stringify({username, password, timezone: clientTimezone()})
  });
}

export async function register(username, email, password, captchaToken, captchaAnswer) {
  return request(`/auth.php?action=register`, {
    method: "POST",
    body: JSON.stringify({
      username, email, password,
      captcha_token: captchaToken,
      captcha_answer: captchaAnswer,
      timezone: clientTimezone()
    })
  });
}

// ---------- CAPTCHA ----------
export async function fetchCaptcha() {
  return request(`/captcha.php?action=challenge`);
}

// ---------- Forgot / reset password ----------
export async function requestPasswordReset(identifier, captchaToken, captchaAnswer) {
  return request(`/password_reset.php?action=request`, {
    method: "POST",
    body: JSON.stringify({
      identifier,
      captcha_token: captchaToken,
      captcha_answer: captchaAnswer
    })
  });
}

export async function verifyResetToken(token) {
  return request(`/password_reset.php?action=verify&token=${encodeURIComponent(token)}`);
}

export async function submitPasswordReset(token, password) {
  return request(`/password_reset.php?action=reset`, {
    method: "POST",
    body: JSON.stringify({token, password})
  });
}

// ---------- Admin endpoints (no fallback — admin requires live API) ----------

export async function adminListBlogs(token, {offset = 0, limit = 20, q = ""} = {}) {
  const qs = new URLSearchParams({offset: String(offset), limit: String(limit)});
  if (q) qs.set("q", q);
  return request(`/admin.php?action=list&${qs.toString()}`, {}, token);
}

export async function adminCreateBlog(payload, token) {
  return request(
    `/admin.php?action=create`,
    {method: "POST", body: JSON.stringify(payload)},
    token
  );
}

export async function adminUpdateBlog(id, payload, token) {
  return request(
    `/admin.php?action=update&id=${id}`,
    {method: "POST", body: JSON.stringify(payload)},
    token
  );
}

export async function adminDeleteBlog(id, token) {
  return request(
    `/admin.php?action=delete&id=${id}`,
    {method: "POST"},
    token
  );
}

// Quick publish/unpublish toggle from the posts list (Task 3 draft workflow)
// — doesn't require opening the full editor.
export async function adminSetBlogStatus(id, status, token) {
  return request(
    `/admin.php?action=blog_set_status&id=${id}`,
    {method: "POST", body: JSON.stringify({status})},
    token
  );
}

// ---------- Media (binary stored in MySQL) ----------
// NOTE: uploads use FormData, so we must NOT go through request() (which forces
// Content-Type: application/json). The browser sets the multipart boundary.
export async function uploadMedia(file, token) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`${API_BASE}/media.php?action=upload`, {
    method: "POST",
    headers: token ? {Authorization: `Bearer ${token}`} : {},
    body: fd,
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new Error(body?.message || `Upload failed (${res.status})`);
  return body; // { id, url, filename, mime, size }
}

export async function adminListMedia(token) {
  return request(`/media.php?action=list`, {}, token);
}

export async function adminDeleteMedia(id, token) {
  return request(`/media.php?action=delete`, {method: "POST", body: JSON.stringify({id})}, token);
}

// ---------- Portfolio content (DB-backed, static fallback) ----------
export async function fetchPortfolio() {
  // Public. Never throws — returns {portfolio:null, offline:true} on any failure
  // so the provider can fall back to the static config.js defaults.
  try {
    const res = await request(`/portfolio.php`);
    return {portfolio: res?.portfolio ?? null, offline: false};
  } catch (e) {
    return {portfolio: null, offline: true};
  }
}

// ---------- AI assistant (Agentforce proxy — see api/chat.php) ----------
// `quiet: true` because an unconfigured/offline chatbot must never trip the
// site-wide maintenance banner; the widget handles its own fallback UX.

export async function chatStart() {
  return request(`/chat.php`, {
    method: "POST",
    quiet: true,
    body: JSON.stringify({action: "start"})
  });
}

export async function chatSend(sessionId, text, sequenceId) {
  return request(`/chat.php`, {
    method: "POST",
    quiet: true,
    body: JSON.stringify({action: "message", sessionId, text, sequenceId})
  });
}

// ---------- Admin: Agentforce connection settings ----------
export async function adminGetAgentforceConfig(token) {
  return request(`/admin.php?action=agentforce_get`, {}, token);
}

export async function adminUpdateAgentforceConfig(config, token) {
  return request(
    `/admin.php?action=agentforce_update`,
    {method: "POST", body: JSON.stringify(config)},
    token
  );
}

export async function adminTestAgentforceConnection(config, token) {
  return request(
    `/admin.php?action=agentforce_test`,
    {method: "POST", body: JSON.stringify(config)},
    token
  );
}

export async function adminGetPortfolio(token) {
  return request(`/admin.php?action=portfolio_get`, {}, token);
}

export async function adminUpdatePortfolio(portfolio, token) {
  return request(
    `/admin.php?action=portfolio_update`,
    {method: "POST", body: JSON.stringify({portfolio})},
    token
  );
}
